import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';

/**
 * Tabs component with exact Hyperkit styling
 *
 * Hyperkit Specs (Node ID: 216:1043):
 * - Sizes: Small (py-8px), Medium (py-14px)
 * - States: Default (grey-500), Hover (grey-900), Selected (grey-950 + 1.5px border)
 * - Optional: Icon, Badge/number
 */
const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      'inline-flex items-center gap-1 border-b border-grey-200 dark:border-grey-800',
      className
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const tabTriggerVariants = cva(
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grey-950/20 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      size: {
        sm: 'py-2 px-1',
        default: 'py-3.5 px-1',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

const TabsTrigger = React.forwardRef(({ className, size, icon, badge, children, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      tabTriggerVariants({ size }),
      'text-grey-500 hover:text-grey-900 data-[state=active]:text-grey-950 data-[state=active]:border-b-[1.5px] data-[state=active]:border-grey-950 data-[state=active]:-mb-px',
      'dark:text-grey-400 dark:hover:text-grey-200 dark:data-[state=active]:text-grey-50 dark:data-[state=active]:border-grey-50',
      className
    )}
    {...props}
  >
    {icon && <span className="shrink-0">{icon}</span>}
    {children}
    {badge !== undefined && (
      <span className={cn(
        'h-5 min-w-[20px] px-1 rounded text-xs font-medium flex items-center justify-center',
        'bg-black/5 text-grey-500',
        'data-[state=active]:bg-grey-900 data-[state=active]:text-white',
        'dark:bg-white/10 dark:text-grey-400',
        'dark:data-[state=active]:bg-grey-50 dark:data-[state=active]:text-grey-950'
      )}>
        {badge}
      </span>
    )}
  </TabsPrimitive.Trigger>
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grey-950/20',
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent, tabTriggerVariants };
